import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { focusRing } from "@/components/Onboarding/ui/buttons";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  "aria-label": string;
  /**
   * `track` (default): a 4 px track with the active option raised white.
   * `primary`: the sketch's period picker, a white group with the active
   * option filled in the brand colour.
   */
  variant?: "track" | "primary";
}

/** Two or three mutually exclusive modes; the active one is raised or filled. */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  "aria-label": ariaLabel,
  variant = "track",
}: SegmentedControlProps<T>) {
  const primary = variant === "primary";
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex rounded-g-sm p-1",
        primary ? "bg-g-bg-card shadow-g-card" : "bg-g-bg-track",
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex items-center gap-1.5 transition-colors duration-150 ease-out",
              primary
                ? "h-8 rounded-[9px] px-3.5 text-[13px] font-bold"
                : "h-[34px] rounded-g-xs px-3.5 text-[13.5px] font-semibold",
              active
                ? primary
                  ? "bg-g-primary text-g-on-primary shadow-g-button"
                  : "bg-g-bg-card text-g-ink-900 shadow-g-card"
                : primary
                  ? "text-g-ink-700 hover:text-g-ink-900"
                  : "text-g-ink-500 hover:text-g-ink-900",
              focusRing,
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
